import { DispatchLeg } from './Endpoints/Dispatch/types';
import { Leg } from './Endpoints/Leg';
import { MutatableLegRaw, PatchLegOptimisticExtensions } from './Endpoints/Leg/types';
import { parseIsoDate } from './types';

/**
 * Method used for mapping a Leg type to a DispatchLeg type for optimistic updates when mutating the cache.
 */
export function legToDispatchLegOptimistic(leg: MutatableLegRaw, optimisticExtensions?: PatchLegOptimisticExtensions) {
  const updates: Partial<DispatchLeg> = {};

  Object.keys(leg).forEach((key) => {
    // key is a string, but it is also a key that can be used to index the type
    const asKey = key as keyof MutatableLegRaw;
    switch (asKey) {
      case 'priority':
        updates.legPriority = leg[asKey];
        break;

      case 'id':
        updates.legId = leg[asKey];
        break;

      case 'no':
        updates.legNo = leg[asKey];
        break;

      case 'cntrId':
        updates.cntrId = leg[asKey];
        break;

      case 'drvId':
        updates.drvId = leg[asKey] ? leg[asKey] : undefined;
        break;

      case 'schdPuFrom':
        updates.scheduledPickupDateFrom = parseIsoDate(leg[asKey]);
        break;

      case 'schdPuTo':
        updates.scheduledPickupDateTo = parseIsoDate(leg[asKey]);
        break;

      case 'schdDelvFrom':
        updates.scheduledDeliveryDateFrom = parseIsoDate(leg[asKey]);
        break;

      case 'schdDelvTo':
        updates.scheduledDeliveryDateTo = parseIsoDate(leg[asKey]);
        break;

      case 'delvId':
        updates.delvId = leg[asKey];
        updates.delvName = optimisticExtensions?.optimisticDelvLoc?.name;
        updates.delvCity = optimisticExtensions?.optimisticDelvLoc?.city;
        updates.delvState = optimisticExtensions?.optimisticDelvLoc?.state;
        break;

      case 'puId':
        updates.puId = leg[asKey];
        updates.puName = optimisticExtensions?.optimisticPuLoc?.name;
        updates.puCity = optimisticExtensions?.optimisticPuLoc?.city;
        updates.puState = optimisticExtensions?.optimisticPuLoc?.state;
        break;

      /**
       * TODO: Ensure the rest of the available optimistic updates are implemented
       * 	Due to time constraint I cannot finish this right now.
       */

      default:
        updates[key] = leg[asKey];
        break;
    }
  });

  return updates;
}

/**
 * Method used for mapping the full Leg response to a Dispatch Leg
 */
export function legToDispatchLeg(leg: Leg) {
  const updates: Partial<DispatchLeg> = {
    legId: leg.id,
    legNo: leg.no,
    legStatus: leg.status,
    legType: leg.type,
    legPriority: leg.priority,
    isActiveLeg: leg.isActiveLeg,
    isHazmat: leg.isHazmat,
    isInvoiced: leg.isInvoiced,
    isLive: leg.csn.isLive,
    isReminder: leg.isReminder,
    isRailBilled: leg.csn.isRailBilled,
    reqHazmatCert: leg.reqHazmatCert,
    hasNotes: (leg.notes?.length || 0) > 0 || (leg.drvNotes?.length || 0) > 0,
    relNo: leg.relNo,
    // releaseNo: leg.
    // bookingRefNo:

    fullAvailableDate: leg.fullAvailableDate,
    emptyAvailableDate: leg.emptyAvailableDate,
    chassisAvailableDate: leg.chassisAvailableDate,

    cntrId: leg.cntrId,
    cntrSize: leg.cntrSize ? Number(leg.cntrSize) : undefined,
    cntrType: leg.cntrType,
    totWgt: leg.wgtLb ?? undefined,
    wgtLb: leg.wgtLb ?? undefined,
    pcs: leg.pcs ?? undefined,
    puSealNo: leg.puSealNo,
    cntrProviderId: leg.cntrProviderId,
    cntrProviderName: leg.cntrProviderName,

    chassisId: leg.chassisId ?? undefined,

    jobId: leg.jobId,
    jobCat: leg.job?.cat,
    jobCategoryTransportationType: leg.job?.catTransType,
    jobDocDate: leg.job?.docDate,
    csnId: leg.csnId,
    csnNo: leg.csn.no,
    csnIsWorkflow: leg.csn.isWorkflow,
    isOverweight: leg.csn.isOverweight,
    masterBol: leg.csn.masterBol,
    bookingRefNo: leg.job?.bookingRefNo,
    csnCustRefNo: leg.csn.custRefNo,
    lastFreeDayDate: leg.lastFreeDate,
    cutoffDate: leg.cutoffDate,

    hubId: leg.hubId ?? undefined,

    // houseBol: leg.job

    drvId: leg.drvId,
    drvName: leg.drv?.name,
    drvType: leg.drv?.type,
    drvCellPhone: leg.drv?.cellPhone,
    drvEmailAddr: leg.drv?.emailAddr,
    puDelvReceiptSent: leg.puDelvReceiptSent,
    tractorId: leg.tractorId,

    scheduledPickupDateFrom: leg.scheduledPickupDateFrom,
    scheduledPickupDateTo: leg.scheduledPickupDateTo,
    pickupDate: leg.pickupDate,
    puId: leg.puId,
    puName: leg.pickupLocation?.name,
    puCity: leg.pickupLocation?.city,
    puState: leg.pickupLocation?.state,
    puRefNo: leg.csn.puRefNo,

    scheduledDeliveryDateFrom: leg.scheduledDeliveryDateFrom,
    scheduledDeliveryDateTo: leg.scheduledDeliveryDateTo,
    deliverDate: leg.delvDate,
    delvId: leg.delvId,
    delvName: leg.deliveryLocation?.name,
    delvCity: leg.deliveryLocation?.city,
    delvState: leg.deliveryLocation?.state,
    delvRefNo: leg.csn.delvRefNo,

    custId: leg.custId,
    custName: leg.cust?.name,
    custEmailAddr: leg.cust?.emailAddr,
    custPhone: leg.cust?.phone,

    houseBol: leg.houseBol

    // Trace values cannot be mapped at this time
  };

  return updates;
}
